import Axios from 'axios';
import React from 'react'
import { useState, useEffect, useContext } from 'react'
import { Country, State, City } from "country-state-city"
import Select from "react-select"
import { EditOutlined } from "@material-ui/icons"
import { storage } from "../../firebase"
import Compressor from 'compressorjs'
import Resizer from "react-image-file-resizer";

import {AuthContext} from '../../context/AuthContext'

import './editProfileForm.css'
import { CircularProgress } from '@material-ui/core';

const EditProfileForm = () => {

  //CONSTANTS
  const SEASONS = [
    {
      label: 'Winter',
      value: 0
    },
    {
      label: 'Autumn',
      value: 1
    },
    {
      label: 'Spring',
      value: 2
    },
    {
      label: 'Summer',
      value: 3
    },
  ]

  //Environment variables
  const PF = process.env.REACT_APP_PUBLIC_FOLDER

  const API = process.env.REACT_APP_API_URL

  //Initializing states
  const [data, setData] = useState({
    username: "",
    profilePicture: "",
    bio: "",
    from_country: "",
    from_state: "",
    from_city: "",
    living_in_country: "",
    living_in_state: "",
    living_in_city: "",
    fav_season: ""
  });

  const [file, setFile] = useState(null)

  const [count, setCount] = useState(0)

  const [progress, setProgress] = useState(0)

  const [season, setSeason] = useState('')

  const [error, setError] = useState("")

  const [success, setSuccess] = useState("")

  const [originAddress, setOriginAddress] = useState({
    country: "",
    state: "",
    city: "",
  })

  const [currentAddress, setCurrentAddress] = useState({
    country: "",
    state: "",
    city: ""
  })

  const { user, dispatch} = useContext(AuthContext)

  //Uploading to the DB
  useEffect(() => {
    const updateUser = async () => {
      if(count > 1){
        try {
          //Uploading the data to MongoDB
          const {data: {msg: msg}} = await Axios.patch(API + `users/${user._id}`, {
            userId: user._id,
            ...data
          })
          dispatch({
            type: 'UPDATE_USER',
            payload: {...user, ...data}
          })
          setSuccess(msg)
          setError("")
          setTimeout(() => {
            setSuccess("")
            setError("")
          }, 3000)
        } catch (error) {
          if(error.response && (error.response.status >= 400 && error.response.status <= 500)){
            setError(error.response.data.msg)
            setSuccess("")
            setTimeout(() => {
              setError("")
              setSuccess("")
            }, 3000)
          }
        }
      }
      else{
        setCount(count + 1)
      }
    }
    updateUser()
  }, [data.profilePicture])

  //Function that resizes a file 
  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        300,
        300,
        "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64"
      );
    }
  );

  //Getting the countries
  const countriesList = () => {
    const countries = Country.getAllCountries()
    const countriesList = countries.map((country) => ({
      label: country.name,
      value: country.isoCode,
      ...country
    }))
    return countriesList
  }

  //Getting the states in the selected country
  const statesList = (countryIsoCode) => {
    const states = State.getStatesOfCountry(countryIsoCode)
    const statesList = states.map((state) => ({
      label: state.name,
      value: state.isoCode,
      ...state
    }))
    return statesList 
  }

  //Getting the cities in the selected state
  const citiesList = (countryIsoCode, stateCode) => {
    const cities = City.getCitiesOfState(countryIsoCode, stateCode)
    const citiesList = cities.map((city) => ({
      label: city.name,
      value: city.name,
      ...city
    }))
    return citiesList
  }

  //Fetching user info on first render
  useEffect(() => {
    const fetchUserInfo = async () => {
      const {
        username,
        bio,
        profilePicture,
        from_country,
        from_state,
        from_city,
        living_in_country,
        living_in_state,
        living_in_city,
        fav_season
      } = user
      //Setting the data to fill the inputs
      setData({
        ...data,
        username,
        profilePicture,
        bio,
        from_country,
        from_state,
        from_city,
        living_in_country,
        living_in_state,
        living_in_city,
        fav_season
      })
      //Setting the season to fill the select component
      setSeason(SEASONS.find((season) => season.label === fav_season))
      //Setting the addresses to fill the select component
      setOriginAddress(() => {
        const country = Country.getCountryByCode(from_country) 
        const state = country ? State.getStateByCodeAndCountry(from_state, from_country) : ""
        const city = state ? City.getCitiesOfState(country.isoCode, state.isoCode).find(({name}) => name === from_city) : ""
        return ({
          country: {
            label: country && country.name,
            value: country && country.isoCode,
            ...country
          },
          state: {
            label: state && state.name,
            value: state && state.isoCode,
            ...state
          },
          city: {
            label: city && city.name,
            value: city && city.name,
            ...city
          }
        })
      })
      //Setting the addresses to fill the select component
      setCurrentAddress(() => {
        const country = Country.getCountryByCode(living_in_country)
        const state = country ? State.getStateByCodeAndCountry(living_in_state, living_in_country) : null
        const city = state ? City.getCitiesOfState(country.isoCode, state.isoCode).find(({name}) => name === living_in_city) : null
        return ({
          country: {
            label: country && country.name,
            value: country && country.isoCode,
            ...country
          },
          state: {
            label: state && state.name,
            value: state && state.isoCode,
            ...state
          },
          city: {
            label: city && city.name,
            value: city && city.name,
            ...city
          }
        })
      })
    }
    fetchUserInfo()
  }, [])

  //Handling input change
  const handleChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value
    })
  }

  //Handling the form submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      //Checking if the user wanted to change his profile picture
      if(file){
        //Uploading the profile picture to Firebase
        const uploadTask = storage.ref(`users/${file.name}`).put(file)
        uploadTask.on(
            'state_changed',
            (snapshot) => {  
              const progressPercentage = snapshot.bytesTransferred * 100 / snapshot.totalBytes
              setProgress(() => progressPercentage)
            },
            (error) => { console.log(error) },
            async () => { 
              const downloadURL = await storage.ref('users').child(file.name).getDownloadURL()
              setData({...data, profilePicture: downloadURL})
              setProgress(0)
            }
        )
      }
      else{
        //Uploading the data to MongoDB
        const {data: {msg: msg}} = await Axios.patch(API + `users/${user._id}`, {
          userId: user._id,
          ...data
        })
        dispatch({
          type: 'UPDATE_USER',
          payload: {...user, ...data}
        })
        setSuccess(msg)
        setError("")
        setTimeout(() => {
          setSuccess("")
          setError("")
        }, 3000)
      }
    } catch (error) {
      if(error.response && (error.response.status >= 400 && error.response.status <= 500)){
        setError(error.response.data.msg)
        setSuccess("")
        setTimeout(() => {
          setError("")
          setSuccess("")
        }, 3000)
      }
    }
  }

  //Handling the image change
  const handleFileChange = async (e) => {
    if(e.target.files[0]){
      try {
        //Compressing the image for less storage space in Firebase (A.K.A don't wanna pay lol)
        const resizedImage = await resizeFile(e.target.files[0])
        const res = await fetch(resizedImage)
        const blob = await res.blob()
        const resizedFile = new File([blob], `${user.username}_${user._id}_profile_picture_${Date.now()}`, { type: 'image/png' })
        new Compressor(resizedFile, {
          quality: 0.7,
          success : (compressedFile) => {
            setFile(compressedFile)
          }
        })
      } catch (error) {
        
      }
    }
  }

  //Handling the button click
  const handleButtonClick = () => {
    document.getElementById("profilePictureInput").click()
  }

  return (
    <div className="formWrapper">
      <form className="form" onSubmit={handleSubmit}>
        <div className="form-group w-100 d-flex flex-column align-items-center">
          <img className='profile_picture' src={file ? URL.createObjectURL(file) : data.profilePicture || (PF + 'person/default_avatar.jpg')}/>
          <div className="edit_btn" onClick={handleButtonClick}>
            <EditOutlined />
            <input type="file" style={{display: 'none'}} id="profilePictureInput" accept=".png, .jpeg, .jpg" onChange={handleFileChange}></input>
          </div>
        </div>
        <div className="inline-group">
        {
          error &&
          <div className="alert alert-danger w-100 text-center" role="alert">
              {error}
          </div>
        }
        {
          success &&
          <div className="alert alert-success w-100 text-center" role="alert">
              {success}
          </div>
        }
        </div>
        <div className="inline-group">
          <div className="form-group sub mr-2">
            <label htmlFor="username">Username</label>
            <input type="text" className="form-control" name='username' value={data.username} onChange={handleChange}/>
          </div>
          <div className="form-group sub">
            <label htmlFor="fav_season">Favorite Season</label>
            <Select 
              style={{border: '1px solid black'}}
              name='fav_season'
              placeholder='Season'
              options={SEASONS}
              value={season}
              onChange={(value) => {
                setData({
                  ...data,
                  fav_season: value.label
                })
                setSeason({
                  label: value.label,
                  value: value.value
                })
              }}
            />
            
          </div>
        </div>
        {/* <div className="form-group">
          <label htmlFor="email">Email</label>
          <input type="text" className="form-control" name='email' value={data.email} onChange={handleChange}/>
        </div> */}
        <div className="form-group">
          <label htmlFor="bio">Bio</label>
          <textarea className="form-control text-area" name='bio' value={data.bio} onChange={handleChange}/>
        </div>
        {/* -------------------- From -------------------- */}
        <div className="inline-group">
          <div className="form-group sub mr-2">
            <label htmlFor="from_country">From</label>
            <Select 
              name='from_country'
              placeholder='Country'
              options={countriesList()}
              value={originAddress.country}
              onChange={(value) => {
                setData({
                  ...data,
                  from_country: value.isoCode
                })
                setOriginAddress({
                  country: value,
                  state: null,
                  city: null
                })
              }}
            />
          </div>
          <div className="form-group sub mr-2">
            <label htmlFor="from_state"></label>
            <Select 
              name='from_state'
              placeholder='State'
              options={originAddress.country ? statesList(originAddress.country.isoCode) : []}
              value={originAddress.state}
              onChange={(value) => {
                setData({
                  ...data,
                  from_state: value.isoCode
                })
                setOriginAddress({
                  ...originAddress,
                  state: value,
                  city: null
                })
              }}
            />
          </div>

          <div className="form-group sub">
            <label htmlFor="from_city"></label>
            <Select 
              name='from_city'
              placeholder='City'
              options={originAddress.state ? citiesList(originAddress.country.isoCode, originAddress.state.isoCode) : []}
              value={originAddress.city}
              onChange={(value) => {
                setData({
                  ...data,
                  from_city: value.name
                })
                setOriginAddress({
                  ...originAddress,
                  city: value
                })
              }}
            />
          </div>
        </div>
        {/* -------------------- Living in -------------------- */}
        <div className="inline-group">
          <div className="form-group sub mr-2 p-0">
            <label htmlFor="living_in_country">Living In</label>
            <Select 
              name='living_in_country'
              placeholder='Country'
              options={countriesList()}
              value={currentAddress.country}
              onChange={(value) => {
                setData({
                  ...data,
                  living_in_country: value.isoCode
                })
                setCurrentAddress({
                  country: value,
                  state: null,
                  city: null
                })
              }}
            />
          </div>
          <div className="form-group sub mr-2 p-0">
            <label htmlFor="living_in_state"></label>
            <Select 
              name='living_in_state'
              placeholder='State'
              options={currentAddress.country ? statesList(currentAddress.country.isoCode) : []}
              value={currentAddress.state}
              onChange={(value) => {
                setData({
                  ...data,
                  living_in_state: value.isoCode
                })
                setCurrentAddress({
                  ...currentAddress,
                  state: value,
                  city: null
                })
              }}
            />
          </div>
          
          <div className="form-group sub">
            <label htmlFor="living_in_city"></label>
            <Select 
              name='living_in_city'
              placeholder='City'
              options={currentAddress.state ? citiesList(currentAddress.country.isoCode, currentAddress.state.isoCode) : []}
              value={currentAddress.city}
              onChange={(value) => {
                setData({
                  ...data,
                  living_in_city: value.name
                })
                setCurrentAddress({
                  ...currentAddress,
                  city: value
                })
              }}
            />
          </div>
        </div>
        <div className="form-group text-center mb-0">
            <button type='submit' className='submit-btn'>
            {
              progress === 0 
              ?
                "Submit"
              :
                <CircularProgress variant='determinate' value={progress} color={"inherit"}/>
            }
            </button>
        </div>
      </form>
    </div>
  )
}

export default EditProfileForm